import React, { useRef, useCallback } from 'react'
import toast from 'react-hot-toast'
import {
  PrinterIcon,
  QrCodeIcon,
  IdentificationIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import QRCodeGenerator from '../QRCodeGenerator'
import ModernDigitalIDCard from '../ModernDigitalIDCard'
import { useMutation, useQueryClient } from 'react-query'
import { residentsAPI } from '../../../services/api'

const PrintModal = ({ resident, isOpen, onClose }) => {
  const qrCodeRef = useRef(null)
  const bothCardsRef = useRef(null)
  const digitalIdRef = useRef(null)
  const queryClient = useQueryClient()
  const householdQrRef = useRef(null)

  const qrData = {
    id: resident?.id || '',
    barangayId: resident?.barangay_id_number || '',
    name: `${resident?.first_name || ''} ${resident?.last_name || ''}`.trim(),
    birthdate: resident?.date_of_birth || resident?.birthdate || '',
    address: `${resident?.purok_zone || ''} ${resident?.barangay || ''} ${
      resident?.municipality || ''
    }`.trim(),
    contactNumber: resident?.phone_number || resident?.contact_number || '',
    emergencyContact: resident?.emergency_contact_name || '',
    emergencyContactNumber: resident?.emergency_contact_number || '',
  }

  const isHead = resident?.is_household_head

  let householdHead = ''
  let householdMembers = []

  if (isHead) {
    householdHead = resident?.full_name
    householdMembers = resident?.household_members || []
  } else {
    const members = resident?.household_members || []

    const headFromMembers = members.find((m) => m.is_household_head === true)

    householdHead = headFromMembers?.full_name || 'N/A'

    // include current resident as member
    householdMembers = [
      ...members.filter((m) => !m.is_household_head),
      resident,
    ]
  }
  const householdQrData = {
    type: 'HOUSEHOLD',
    head: householdHead,
    members: householdMembers.map((m) => ({
      id: m?.id,
      name: m?.full_name,
    })),
    totalMembers: householdMembers.length,
    barangayId: resident?.barangay_id_number,
    location: {
      barangay: resident?.barangay,
      municipality: resident?.municipality,
      province: resident?.province,
    },
    generatedAt: new Date().toISOString(),
  }
  const residentQrPayload = `RES|${resident?.barangay_id_number || ''}|${
    resident?.first_name || ''
  } ${resident?.last_name || ''}|${resident?.date_of_birth || ''}|${
    resident?.purok_zone || ''
  } ${resident?.barangay || ''} ${resident?.municipality || ''}|${
    resident?.phone_number || resident?.contact_number || ''
  }|${new Date().toISOString()}`

  const householdQrPayload = `HH|${
    resident?.barangay_id_number
  }|${householdHead}|${householdMembers
    .map((m) => m?.full_name)
    .join(',')}|${`${
    resident?.full_address || ''
  }  `.trim()}|${new Date().toISOString()}`

  // Iframe-based print method for QR Code
  const iframePrintQR = useCallback(() => {
    try {
      const canvas = qrCodeRef.current?.querySelector('canvas')
      if (!canvas) {
        toast.error('QR Code not ready')
        return
      }
      const dataURL = canvas.toDataURL('image/png')
      let printFrame = window.document.getElementById('qr-print-iframe')
      if (!printFrame) {
        printFrame = window.document.createElement('iframe')
        printFrame.id = 'qr-print-iframe'
        printFrame.style.position = 'fixed'
        printFrame.style.right = '0'
        printFrame.style.bottom = '0'
        printFrame.style.width = '0'
        printFrame.style.height = '0'
        printFrame.style.border = 'none'
        printFrame.style.visibility = 'hidden'
        window.document.body.appendChild(printFrame)
      }
      const doc = printFrame.contentWindow.document
      doc.open()
      doc.write(`
        <html>
        <head>
          <title>QR Code - ${resident?.barangay_id_number || 'unknown'}</title>
          <style>
            @page { size: A4 portrait; margin: 1in; }
            body { margin: 0; padding: 20px; text-align: center; font-family: Arial, sans-serif; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .qr-container { max-width: 6.5in; margin: 0 auto; padding: 30px; border: 2px solid #1D4ED8; border-radius: 12px; background: white; }
            .qr-title { font-size: 24px; font-weight: bold; color: #1E3A8A; margin-bottom: 20px; text-transform: uppercase; }
            .qr-subtitle { font-size: 16px; color: #6B7280; margin-bottom: 30px; }
            .qr-image { width: 300px; height: 300px; margin: 20px auto; border: 1px solid #E5E7EB; border-radius: 8px; background: white; }
            .qr-info { margin-top: 30px; padding-top: 20px; border-top: 2px solid #E5E7EB; }
            .resident-name { font-size: 20px; font-weight: bold; color: #1F2937; margin-bottom: 15px; }
            .info-table { margin: 0 auto; text-align: left; max-width: 400px; }
            .info-row { display: flex; margin-bottom: 8px; font-size: 14px; }
            .info-label { font-weight: bold; color: #374151; min-width: 120px; flex-shrink: 0; }
            .info-value { color: #1F2937; flex: 1; }
            .footer-note { margin-top: 30px; padding-top: 20px; border-top: 1px solid #E5E7EB; font-size: 12px; color: #6B7280; }
            @media print { body { margin: 0; padding: 10px; } .qr-container { border-color: #1D4ED8 !important; } }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="qr-title">QR Code</div>
            <div class="qr-subtitle">Resident Information</div>
            <img src="${dataURL}" alt="QR Code" class="qr-image" />
            <div class="qr-info">
              <div class="resident-name">${resident.first_name} ${
        resident.last_name
      }</div>
              <div class="info-table">
                <div class="info-row"><div class="info-label">Wallet ID:</div><div class="info-value">${
                  resident.wallet_address || 'N/A'
                }</div></div>
                <div class="info-row"><div class="info-label">ID Number:</div><div class="info-value">${
                  resident.barangay_id_number || 'N/A'
                }</div></div>
                <div class="info-row"><div class="info-label">Barangay:</div><div class="info-value">${
                  resident.barangay || 'N/A'
                }</div></div>
                <div class="info-row"><div class="info-label">Municipality:</div><div class="info-value">${
                  resident.municipality || 'N/A'
                }</div></div>
                <div class="info-row"><div class="info-label">Province:</div><div class="info-value">${
                  resident.province || 'N/A'
                }</div></div>
                <div class="info-row"><div class="info-label">Contact:</div><div class="info-value">${
                  resident.phone_number || resident.contact_number || 'N/A'
                }</div></div>
                <div class="info-row"><div class="info-label">Generated:</div><div class="info-value">${new Date().toLocaleDateString()}</div></div>
              </div>
            </div>
            <div class="footer-note"><p>This QR code contains resident information for verification purposes.</p><p>Valid for barangay transactions and identification.</p></div>
          </div>
          <script>
            window.onload = function() {
              const img = document.querySelector('.qr-image');
              if (img.complete) {
                setTimeout(function() { window.print(); }, 500);
              } else {
                img.onload = function() { setTimeout(function() { window.print(); }, 500); };
                setTimeout(function() { window.print(); }, 3000);
              }
            };
          </script>
        </body>
        </html>
      `)
      doc.close()
      printFrame.contentWindow.focus()
      toast.success('QR Code print initiated')
    } catch (error) {
      console.error('Print error:', error)
      toast.error('Failed to print QR Code')
    }
  }, [resident, qrCodeRef])
  const iframePrintQRHH = useCallback(() => {
    try {
      const canvas = householdQrRef.current?.querySelector('canvas')
      if (!canvas) {
        toast.error('QR Code not ready')
        return
      }

      const dataURL = canvas.toDataURL('image/png')

      let householdHead = ''
      let householdMembers = []

      if (resident?.is_household_head) {
        householdHead = resident?.full_name
        householdMembers = resident?.household_members || []
      } else {
        const members = resident?.household_members || []
        const head = members.find((m) => m.is_household_head)

        householdHead = head?.full_name || 'N/A'

        householdMembers = [
          ...members.filter((m) => !m.is_household_head),
          resident,
        ]
      }

      const membersHTML =
        householdMembers.length > 0
          ? householdMembers
              .map((m) => `<div class="member-line">${m.full_name}</div>`)
              .join('')
          : `<div class="member-line">None</div>`

      let printFrame = document.getElementById('qr-print-iframe')

      if (!printFrame) {
        printFrame = document.createElement('iframe')
        printFrame.id = 'qr-print-iframe'
        printFrame.style.position = 'fixed'
        printFrame.style.right = '0'
        printFrame.style.bottom = '0'
        printFrame.style.width = '0'
        printFrame.style.height = '0'
        printFrame.style.border = 'none'
        printFrame.style.visibility = 'hidden'
        document.body.appendChild(printFrame)
      }

      const doc = printFrame.contentWindow.document
      doc.open()
      doc.write(`
      <html>
      <head>
        <title>QR Code - ${resident?.barangay_id_number || 'unknown'}</title>
        <style>
          @page { size: A4 portrait; margin: 1in; }

            body { margin: 0; padding: 20px; text-align: center; font-family: Arial, sans-serif; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .qr-container { max-width: 6.5in; margin: 0 auto; padding: 30px; border: 2px solid #1D4ED8; border-radius: 12px; background: white; }
            .qr-title { font-size: 24px; font-weight: bold; color: #1E3A8A; margin-bottom: 20px; text-transform: uppercase; }
            .qr-subtitle { font-size: 16px; color: #6B7280; margin-bottom: 30px; }

                     .qr-image { width: 300px; height: 300px; margin: 20px auto; border: 1px solid #E5E7EB; border-radius: 8px; background: white; }

            .qr-info { margin-top: 25px; padding-top: 15px; border-top: 2px solid #E5E7EB; }

          .section {
            margin-top: 20px;
            text-align: center;
          }

          .section-title {
            font-weight: bold;
            margin-bottom: 8px;
            font-size: 14px;
          }

          .member-line {
            font-size: 14px;
            margin-left: 15px;
            margin-bottom: 4px;
          }

          .info-row {
            font-size: 14px;
            margin-bottom: 6px;
          }

          .label {
            font-weight: bold;
          }
            .footer-note { margin-top: 30px; padding-top: 20px; border-top: 1px solid #E5E7EB; font-size: 12px; color: #6B7280; }
 
        </style>
      </head>
      <body>
         <div class="qr-container">
            <div class="qr-title">QR Code</div>
            <div class="qr-subtitle">Resident Household Information</div>
          <img src="${dataURL}" class="qr-image" />
          <div class="qr-info">

          <div class="section">
            <div class="section-title">Household Head</div>
            <div class="member-line">${householdHead || 'N/A'}</div>
          </div>

          <div class="section">
<div class="section-title">
  Household Member${householdMembers.length > 1 ? 's' : ''}
</div>            ${membersHTML}
          </div>
           </div>


                     <div class="footer-note"><p>This QR code contains resident information for verification purposes.</p><p>Valid for barangay transactions and identification.</p></div>

        </div>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `)

      doc.close()
      printFrame.contentWindow.focus()

      toast.success('QR Code print initiated')
    } catch (error) {
      console.error('Print error:', error)
      toast.error('Failed to print QR Code')
    }
  }, [resident, householdQrRef])

  // Iframe-based print method for Digital ID (fix: always render back card with qrDataUrl for print)
  const iframePrintDigitalID = useCallback(async () => {
    try {
      const parentHeadMarkup = Array.from(
        window.document.querySelectorAll('link[rel="stylesheet"], style')
      )
        .map((node) => node.outerHTML)
        .join('\n')

      const qrCanvas = qrCodeRef.current?.querySelector('canvas')
      if (!qrCanvas) {
        toast.error('QR Code not ready')
        return
      }
      const qrDataUrl = qrCanvas.toDataURL('image/png')

      let printFrame = window.document.getElementById('digital-id-print-iframe')
      if (!printFrame) {
        printFrame = window.document.createElement('iframe')
        printFrame.id = 'digital-id-print-iframe'
        printFrame.style.position = 'fixed'
        printFrame.style.right = '0'
        printFrame.style.bottom = '0'
        printFrame.style.width = '0'
        printFrame.style.height = '0'
        printFrame.style.border = 'none'
        printFrame.style.visibility = 'hidden'
        window.document.body.appendChild(printFrame)
      }
      const doc = printFrame.contentWindow.document
      doc.open()
      doc.write(`
        <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
          <title>Digital ID - ${
            resident?.barangay_id_number || 'unknown'
          }</title>
          <base href="${window.location.origin}/">
          ${parentHeadMarkup}
          <style>
            @page { size: 8.5in 11in; margin: 0.5in; }
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .id-cards-row { display: flex; flex-direction: row; gap: 32px; justify-content: center; align-items: flex-start; }
            .digital-id-card { width: 3.675in !important; height: 2.325in !important; page-break-inside: avoid; margin-bottom: 0.25in; display: block !important; border: 1px solid #1D4ED8; border-radius: 8px; overflow: hidden; background: #fff; }
            .digital-id-card img { image-rendering: crisp-edges; image-rendering: -webkit-optimize-contrast; }
            h3 { text-align: center; margin-bottom: 15px; font-size: 18px; font-weight: bold; color: black; }
            @media print { body { margin: 0; } .id-cards-row { gap: 16px; } }
          </style>
        </head>
        <body>
          <div id="print-root"></div>
        </body>
        </html>
      `)
      doc.close()

      // Wait for iframe to be ready
      await new Promise((resolve) => {
        printFrame.onload = () => resolve()
        setTimeout(resolve, 250)
      })

      const [{ default: ModernDigitalIDCard }, { createRoot }] =
        await Promise.all([
          import('../ModernDigitalIDCard'),
          import('react-dom/client'),
        ])
      const printDoc = printFrame.contentWindow.document
      const printRoot = printDoc.getElementById('print-root')
      const root = createRoot(printRoot)
      root.render(
        <div className='id-cards-row'>
          <div>
            <div className='digital-id-card'>
              <ModernDigitalIDCard resident={resident} showBack={false} />
            </div>
          </div>
          <div>
            <div className='digital-id-card'>
              <ModernDigitalIDCard
                resident={resident}
                showBack={true}
                qrDataUrl={qrDataUrl}
              />
            </div>
          </div>
        </div>
      )
      await new Promise((resolve) => setTimeout(resolve, 250))
      printFrame.contentWindow.focus()
      printFrame.contentWindow.print()
      toast.success('Digital ID print initiated')
    } catch (error) {
      console.error('Print error:', error)
      toast.error('Failed to print Digital ID')
    }
  }, [resident, digitalIdRef, qrCodeRef])
  const printMutation = useMutation({
    mutationFn: (residentId) => residentsAPI.getPrint(residentId),

    onSuccess: () => {
      // ✅ Refresh resident statistics
      queryClient.invalidateQueries({
        queryKey: ['resident-statistics'],
      })

      toast.success('Print request recorded')
    },

    onError: (error) => {
      console.error('Print error:', error)
      toast.error('Failed to log print request')
    },
  })
  const handlePrint = (printFunction) => {
    if (!resident?.id) {
      toast.error('Resident ID is missing')
      return
    }

    printMutation.mutate(resident.id, {
      onSuccess: () => {
        printFunction()
      },
    })
  }

  if (!isOpen || !resident) return null

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto'>
        {/* Header */}
        <div className='flex justify-between items-center p-6 border-b border-gray-200'>
          <h2 className='text-xl font-semibold text-gray-900'>
            Print Options for {resident.first_name} {resident.last_name}
          </h2>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 transition-colors'
          >
            <XMarkIcon className='w-6 h-6' />
          </button>
        </div>

        <div className='p-6'>
          <div className='grid grid-cols-1 lg:grid-cols-12 gap-8'>
            {/* Digital ID Section */}
            <div className='space-y-4 col-span-6'>
              <div className='border rounded-lg p-4 bg-gray-50'>
                <div className='lg:col-span-3 flex items-center justify-between space-x-4 mb-4'>
                  <div className='flex items-center space-x-2'>
                    <IdentificationIcon className='w-6 h-6 text-gray-700' />
                    <h3 className='text-md font-semibold text-gray-700'>
                      Digital ID Card
                    </h3>
                  </div>

                  <div className='flex space-x-2'>
                    <button
                      disabled={printMutation.isPending}
                      onClick={() => handlePrint(iframePrintDigitalID)}
                      className='flex items-center space-x-1 bg-blue-50 border border-blue-700 text-blue-700 text-sm px-3 py-1.5 rounded-md hover:bg-blue-100 transition-colors'
                    >
                      <PrinterIcon className='w-4 h-4' />
                      <span>Print</span>
                    </button>
                  </div>
                </div>

                <div className='w-full flex flex-col items-center'>
                  <div
                    ref={digitalIdRef}
                    className='flex flex-col items-center space-y-4'
                  >
                    <div data-side='front'>
                      <ModernDigitalIDCard
                        resident={resident}
                        showBack={false}
                      />
                    </div>
                    <div data-side='back'>
                      {/* Generate QR code data URL and pass to back card */}
                      {(() => {
                        const canvas =
                          qrCodeRef.current?.querySelector('canvas')
                        let qrDataUrl = ''
                        if (canvas) {
                          qrDataUrl = canvas.toDataURL('image/png')
                        }
                        return (
                          <ModernDigitalIDCard
                            resident={resident}
                            showBack={true}
                            qrDataUrl={qrDataUrl}
                          />
                        )
                      })()}
                    </div>
                  </div>
                </div>

                {/* Hidden print components */}
                <div
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    top: '-9999px',
                    width: 'auto',
                    height: 'auto',
                  }}
                >
                  <div
                    ref={bothCardsRef}
                    className='id-cards-container'
                    style={{
                      display: 'block',
                      width: 'auto',
                      height: 'auto',
                      backgroundColor: 'white',
                      padding: '20px',
                    }}
                  >
                    <div
                      style={{ marginBottom: '30px', pageBreakInside: 'avoid' }}
                    >
                      <h3
                        style={{
                          textAlign: 'center',
                          marginBottom: '15px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: 'black',
                        }}
                      >
                        Front
                      </h3>
                      <div className='digital-id-card'>
                        <ModernDigitalIDCard
                          resident={resident}
                          showBack={false}
                        />
                      </div>
                    </div>
                    <div style={{ pageBreakInside: 'avoid' }}>
                      <h3
                        style={{
                          textAlign: 'center',
                          marginBottom: '15px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: 'black',
                        }}
                      >
                        Back
                      </h3>
                      <div className='digital-id-card'>
                        <ModernDigitalIDCard
                          resident={resident}
                          showBack={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code Section */}
            <div className='space-y-4 col-span-6'>
              {/* QR Code Preview */}
              <div className='border rounded-lg bg-gray-50 p-3'>
                <div className='lg:col-span-3 flex items-center justify-between space-x-4 mb-4'>
                  <div className='flex items-center space-x-2'>
                    <QrCodeIcon className='w-6 h-6 text-gray-700' />
                    <h3 className='text-md font-semibold text-gray-700'>
                      Resident QR Code
                    </h3>
                  </div>

                  <div className='flex space-x-2'>
                    <button
                      onClick={() => {
                        if (!resident?.barangay_id_number) {
                          toast.error('Resident ID number is missing')
                          return
                        }
                        if (!qrCodeRef.current) {
                          toast.error('QR Code not ready for printing')
                          return
                        }
                        iframePrintQR()
                      }}
                      className='flex items-center space-x-1 bg-blue-50 border border-blue-700 text-blue-700 text-sm px-3 py-1.5 rounded-md transition-colors hover:bg-blue-100'
                    >
                      <PrinterIcon className='w-4 h-4' />
                      <span>Print</span>
                    </button>
                  </div>
                </div>

                <div
                  ref={qrCodeRef}
                  className='print-content text-center bg-white p-8 rounded-lg'
                >
                  <div className='flex justify-center mb-4'>
                    {qrData && (
                      <QRCodeGenerator data={residentQrPayload} size={200} />
                    )}
                  </div>
                  <div className='text-sm text-gray-600 space-y-1'>
                    <p className='font-semibold text-blue-900'>
                      {resident.first_name} {resident.last_name}
                    </p>
                    <p className='text-[11px]'>
                      Wallet ID: {resident.wallet_address || 'N/A'}
                    </p>
                    <p>ID Number: {resident.barangay_id_number}</p>{' '}
                    <p>Barangay: {resident.barangay}</p>
                    <p>Generated: {new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
              <div className='border rounded-lg bg-gray-50 p-3'>
                <div className='lg:col-span-3 flex items-center justify-between space-x-4 mb-4'>
                  <div className='flex items-center space-x-2'>
                    <QrCodeIcon className='w-6 h-6 text-gray-700' />
                    <h3 className='text-md font-semibold text-gray-700'>
                      Household QR Code
                    </h3>
                  </div>

                  <div className='flex space-x-2'>
                    <button
                      onClick={() => {
                        if (!resident?.barangay_id_number) {
                          toast.error('Resident ID number is missing')
                          return
                        }
                        if (!householdQrRef.current) {
                          toast.error('QR Code not ready for printing')
                          return
                        }
                        iframePrintQRHH()
                      }}
                      className='flex items-center space-x-1 bg-blue-50 border border-blue-700 text-blue-700 text-sm px-3 py-1.5 rounded-md transition-colors hover:bg-blue-100'
                    >
                      <PrinterIcon className='w-4 h-4' />
                      <span>Print</span>
                    </button>
                  </div>
                </div>

                <div
                  ref={householdQrRef}
                  className='print-content text-center bg-white p-8 rounded-lg'
                >
                  <div className='flex justify-center mb-4'>
                    {householdQrData && (
                      <QRCodeGenerator data={householdQrPayload} size={200} />
                    )}{' '}
                  </div>
                  <div className='text-sm text-gray-600 space-y-1'>
                    <p>Household Head: {householdHead}</p>

                    <p>
                      Household Member{householdMembers.length > 1 ? 's' : ''}:
                    </p>
                    {householdMembers.length > 0 ? (
                      <div className='mt-1'>
                        {householdMembers.map((m, index) => (
                          <div key={m.id || index}>{m.full_name}</div>
                        ))}
                      </div>
                    ) : (
                      <p>None</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrintModal
